import { useState, useEffect } from 'react';
import { WorldSeed } from '../types';
import { updateWorldSettings, getAvailableModels, ModelInfo } from '../api';
import { useAuthStore } from '../store';

// Font size type (matches NarrativeView)
type FontSize = 'small' | 'medium' | 'large' | 'xlarge';

interface WorldSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  worldId: string;
  worldTitle: string;
  worldSeed: WorldSeed;
  onSettingsUpdated: (updatedSeed: Partial<WorldSeed>, updatedTitle?: string) => void;
  fontSize: FontSize;
  onFontSizeChange: (size: FontSize) => void;
}

type CollapsibleSection = 'general' | 'display' | 'model' | 'content';

const REASONING_EFFORT_OPTIONS = [
  { value: 'minimal', label: 'Minimal - Quick responses' },
  { value: 'low', label: 'Low - Light reasoning' },
  { value: 'medium', label: 'Medium - Balanced' },
  { value: 'high', label: 'High - Deep thinking' },
  { value: 'max', label: 'Max - Deepest thinking' },
] as const;

export function WorldSettingsModal({
  isOpen,
  onClose,
  worldId,
  worldTitle,
  worldSeed,
  onSettingsUpdated,
  fontSize,
  onFontSizeChange,
}: WorldSettingsModalProps) {
  const user = useAuthStore(state => state.user);
  const isAdmin = !!user?.is_admin;

  // Form state
  const [title, setTitle] = useState(worldTitle);
  const [provider, setProvider] = useState(worldSeed.provider ?? '');
  const [model, setModel] = useState(worldSeed.model ?? '');
  const [contentRating, setContentRating] = useState<'standard' | 'mature' | 'explicit'>(worldSeed.content_rating);
  const [allowNsfw, setAllowNsfw] = useState(worldSeed.allow_nsfw);
  const [reasoningEffort, setReasoningEffort] = useState<'minimal' | 'low' | 'medium' | 'high' | 'max' | null>(worldSeed.reasoning_effort);

  // UI state
  const [expandedSections, setExpandedSections] = useState<Set<CollapsibleSection>>(
    new Set(isAdmin ? ['model'] : [])
  );
  const [availableModels, setAvailableModels] = useState<ModelInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  
  // Fetch available models on mount
  useEffect(() => {
    if (isOpen && isAdmin) {
      setIsLoading(true);
      getAvailableModels()
        .then(setAvailableModels)
        .catch(err => {
          console.error('Failed to fetch models:', err);
          setError('Failed to load available models');
        })
        .finally(() => setIsLoading(false));
    }
  }, [isOpen, isAdmin]);

  useEffect(() => {
    if (isOpen && isAdmin) {
      setExpandedSections(prev => {
        if (prev.has('model')) return prev;
        const next = new Set(prev);
        next.add('model');
        return next;
      });
    }
  }, [isOpen, isAdmin]);
  
  // Reset form when worldSeed changes
  useEffect(() => {
    setTitle(worldTitle);
    setProvider(worldSeed.provider ?? '');
    setModel(worldSeed.model ?? '');
    setContentRating(worldSeed.content_rating);
    setAllowNsfw(worldSeed.allow_nsfw);
    setReasoningEffort(worldSeed.reasoning_effort);
    setError('');
  }, [worldSeed, worldTitle]);
  
  if (!isOpen) return null;
  
  const toggleSection = (section: CollapsibleSection) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };
  
  // Get unique providers
  const providers = [...new Set(availableModels.map(m => m.provider))];
  
  // Get models for selected provider
  const modelsForProvider = availableModels.filter(m => m.provider === provider);
  
  // Get selected model info
  const selectedModel = availableModels.find(m => m.provider === provider && m.modelId === model);

  const reasoningEffortOptions = REASONING_EFFORT_OPTIONS.filter(option =>
    !selectedModel?.supportedReasoningEfforts?.length ||
    selectedModel.supportedReasoningEfforts.includes(option.value)
  );
  
  // Handle provider change
  const handleProviderChange = (newProvider: string) => {
    setProvider(newProvider);
    // Auto-select first model for this provider
    const firstModel = availableModels.find(m => m.provider === newProvider);
    if (firstModel) {
      setModel(firstModel.modelId);
    }
    setReasoningEffort(null);
  };

  const handleModelChange = (newModel: string) => {
    const nextModel = availableModels.find(m => m.provider === provider && m.modelId === newModel);
    if (reasoningEffort && nextModel?.supportedReasoningEfforts?.length &&
        !nextModel.supportedReasoningEfforts.includes(reasoningEffort)) {
      setReasoningEffort(null);
    }
    setModel(newModel);
  };
  
  const handleSave = async () => {
    setIsSaving(true);
    setError('');
    
    try {
      const updates: Parameters<typeof updateWorldSettings>[1] = {};
      
      // Only include changed fields
      if (title !== worldTitle) updates.title = title;
      if (isAdmin) {
        if (provider !== worldSeed.provider) updates.provider = provider;
        if (model !== worldSeed.model) updates.model = model;
      }
      if (contentRating !== worldSeed.content_rating) updates.content_rating = contentRating;
      if (allowNsfw !== worldSeed.allow_nsfw) updates.allow_nsfw = allowNsfw;
      if (isAdmin && reasoningEffort !== worldSeed.reasoning_effort) updates.reasoning_effort = reasoningEffort;

      if (Object.keys(updates).length === 0) {
        onClose();
        return;
      }
      
      await updateWorldSettings(worldId, updates);
      
      // Notify parent of updates
      onSettingsUpdated(
        {
          ...(isAdmin
            ? {
                provider: updates.provider || worldSeed.provider,
                model: updates.model || worldSeed.model,
                reasoning_effort:
                  updates.reasoning_effort !== undefined
                    ? updates.reasoning_effort
                    : worldSeed.reasoning_effort,
              }
            : {}),
          content_rating: updates.content_rating || worldSeed.content_rating,
          allow_nsfw: updates.allow_nsfw ?? worldSeed.allow_nsfw,
        },
        updates.title
      );
      
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };
  
  const SectionHeader = ({
    section,
    title,
    icon,
  }: {
    section: CollapsibleSection;
    title: string;
    icon: string;
  }) => (
    <button
      onClick={() => toggleSection(section)}
      className="w-full flex items-center justify-between p-3 bg-dark-700 hover:bg-dark-600 rounded-lg transition-colors"
    >
      <div className="flex items-center gap-2">
        <span>{icon}</span>
        <span className="font-medium text-dark-50">{title}</span>
      </div>
      <svg
        className={`w-5 h-5 text-dark-400 transition-transform ${expandedSections.has(section) ? 'rotate-180' : ''}`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </button>
  );
  
  const formatCost = (input: number, output: number) => {
    return `$${input.toFixed(2)} / $${output.toFixed(2)} per 1M tokens`;
  };
  
  const formatContext = (tokens: number) => {
    if (tokens >= 1000000) return `${(tokens / 1000000).toFixed(1)}M`;
    if (tokens >= 1000) return `${Math.round(tokens / 1000)}K`;
    return tokens.toString();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-dark-800 rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-dark-600">
          <h2 className="text-xl font-semibold text-dark-50 flex items-center gap-2">
            <span>⚙️</span>
            World Settings
          </h2>
          <button
            onClick={onClose}
            className="text-dark-400 hover:text-dark-50 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {error && (
            <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}
          
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="spinner w-8 h-8" />
            </div>
          ) : (
            <>
              {/* General Section */}
              <div className="space-y-2">
                <SectionHeader section="general" title="General" icon="📝" />
                {expandedSections.has('general') && (
                  <div className="pl-4 pr-2 py-3 space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-dark-200 mb-1">
                        World Title
                      </label>
                      <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="w-full bg-dark-700 border border-dark-500 rounded-lg px-3 py-2 text-dark-50 text-sm focus:outline-none focus:border-primary-500"
                        placeholder="Enter world title..."
                      />
                    </div>
                  </div>
                )}
              </div>
              
              {/* Display Section */}
              <div className="space-y-2">
                <SectionHeader section="display" title="Display" icon="🔤" />
                {expandedSections.has('display') && (
                  <div className="pl-4 pr-2 py-3 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-dark-200 mb-2">
                        Font Size
                      </label>
                      <div className="grid grid-cols-4 gap-2">
                        {(['small', 'medium', 'large', 'xlarge'] as const).map((size) => (
                          <button
                            key={size}
                            onClick={() => onFontSizeChange(size)}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                              fontSize === size
                                ? 'bg-primary-600 text-white'
                                : 'bg-dark-700 text-dark-200 hover:bg-dark-600'
                            }`}
                          >
                            {size === 'small' && 'S'}
                            {size === 'medium' && 'M'}
                            {size === 'large' && 'L'}
                            {size === 'xlarge' && 'XL'}
                          </button>
                        ))}
                      </div>
                      <p className="text-dark-400 text-xs mt-2">
                        Adjusts the narrative text size (saved per world)
                      </p>
                    </div>
                  </div>
                )}
              </div>
              
              {/* AI Model Section */}
              {isAdmin && (
                <div className="space-y-2">
                  <SectionHeader section="model" title="AI Model" icon="🤖" />
                  {expandedSections.has('model') && (
                    <div className="pl-4 pr-2 py-3 space-y-4">
                    {/* Provider dropdown */}
                    <div>
                      <label className="block text-sm font-medium text-dark-200 mb-1">
                        Provider
                      </label>
                      <select
                        value={provider}
                        onChange={(e) => handleProviderChange(e.target.value)}
                        className="w-full bg-dark-700 border border-dark-500 rounded-lg px-3 py-2 text-dark-50 text-sm focus:outline-none focus:border-primary-500"
                      >
                        {providers.map(p => (
                          <option key={p} value={p}>
                            {p.charAt(0).toUpperCase() + p.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    {/* Model dropdown */}
                    <div>
                      <label className="block text-sm font-medium text-dark-200 mb-1">
                        Model
                      </label>
                      <select
                        value={model}
                        onChange={(e) => handleModelChange(e.target.value)}
                        className="w-full bg-dark-700 border border-dark-500 rounded-lg px-3 py-2 text-dark-50 text-sm focus:outline-none focus:border-primary-500"
                      >
                        {modelsForProvider.map(m => (
                          <option key={m.id} value={m.modelId}>
                            {m.displayName}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    {/* Model Info Card */}
                    {selectedModel && (
                      <div className="bg-dark-700/50 rounded-lg p-3 space-y-2 text-sm">
                        <div className="flex items-center gap-2 flex-wrap">
                          {selectedModel.categories.map(cat => (
                            <span
                              key={cat}
                              className={`px-2 py-0.5 rounded text-xs font-medium ${
                                cat === 'reasoning' ? 'bg-purple-600/30 text-purple-300' :
                                cat === 'fast' ? 'bg-green-600/30 text-green-300' :
                                cat === 'permissive' ? 'bg-orange-600/30 text-orange-300' :
                                'bg-blue-600/30 text-blue-300'
                              }`}
                            >
                              {cat}
                            </span>
                          ))}
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-dark-300">
                          <div>
                            <span className="text-dark-400">Context:</span>{' '}
                            <span className="text-dark-50">{formatContext(selectedModel.contextWindow)} tokens</span>
                          </div>
                          <div>
                            <span className="text-dark-400">Latency:</span>{' '}
                            <span className="text-dark-50">~{selectedModel.avgLatencyMs}ms</span>
                          </div>
                        </div>
                        <div className="text-dark-400">
                          <span>Cost:</span>{' '}
                          <span className="text-dark-200">{formatCost(selectedModel.pricing.input, selectedModel.pricing.output)}</span>
                        </div>
                        {selectedModel.supportsReasoning && (
                          <div className="text-purple-300 text-xs">
                            ✨ Extended thinking {selectedModel.reasoningMandatory ? 'always on' : 'enabled'}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Reasoning Effort Control */}
                    {selectedModel?.supportsReasoning && (
                      <div>
                        <label className="block text-sm font-medium text-dark-200 mb-2">
                          Thinking Depth
                        </label>
                        <select
                          value={reasoningEffort || ''}
                          onChange={(e) => setReasoningEffort(e.target.value ? e.target.value as any : null)}
                          className="w-full bg-dark-700 border border-dark-500 rounded-lg px-3 py-2 text-dark-50 text-sm focus:outline-none focus:border-primary-500"
                        >
                          <option value="">
                            Auto{selectedModel.defaultReasoningEffort
                              ? ` (${selectedModel.defaultReasoningEffort} default${selectedModel.reasoningMandatory ? ', always on' : ''})`
                              : ' (model decides)'}
                          </option>
                          {reasoningEffortOptions.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                        <p className="text-dark-400 text-xs mt-1">
                          Higher levels use more thinking tokens and may increase cost/latency
                        </p>
                      </div>
                    )}
                    </div>
                  )}
                </div>
              )}
              
              {/* Content Settings Section */}
              <div className="space-y-2">
                <SectionHeader section="content" title="Content Settings" icon="🔒" />
                {expandedSections.has('content') && (
                  <div className="pl-4 pr-2 py-3 space-y-4">
                    {/* Content Rating */}
                    <div>
                      <label className="block text-sm font-medium text-dark-200 mb-2">
                        Content Rating
                      </label>
                      <div className="space-y-2">
                        {(['standard', 'mature', 'explicit'] as const).map(rating => (
                          <label
                            key={rating}
                            className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                              contentRating === rating
                                ? 'bg-primary-600/20 border border-primary-500'
                                : 'bg-dark-700 border border-transparent hover:bg-dark-600'
                            }`}
                          >
                            <input
                              type="radio"
                              name="contentRating"
                              value={rating}
                              checked={contentRating === rating}
                              onChange={() => setContentRating(rating)}
                              className="w-4 h-4 text-primary-500"
                            />
                            <div>
                              <div className="text-dark-50 font-medium capitalize">{rating}</div>
                              <div className="text-dark-400 text-xs">
                                {rating === 'standard' && 'General audience, no explicit content'}
                                {rating === 'mature' && 'Adult themes, violence, suggestive content'}
                                {rating === 'explicit' && 'Explicit content allowed'}
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                    
                    {/* NSFW Fallback Toggle */}
                    <label className="flex items-center justify-between p-3 bg-dark-700 rounded-lg cursor-pointer hover:bg-dark-600 transition-colors">
                      <div>
                        <div className="text-dark-50 font-medium">Allow NSFW Fallback</div>
                        <div className="text-dark-400 text-xs">
                          Use permissive model if primary refuses content
                        </div>
                      </div>
                      <div className="relative">
                        <input
                          type="checkbox"
                          checked={allowNsfw}
                          onChange={(e) => setAllowNsfw(e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-dark-500 rounded-full peer peer-checked:bg-primary-500 transition-colors" />
                        <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-5" />
                      </div>
                    </label>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        
        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-dark-600">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-dark-200 hover:text-dark-50 transition-colors"
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || isLoading}
            className="px-4 py-2 bg-primary-600 hover:bg-primary-500 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSaving && <div className="spinner w-4 h-4" />}
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
